import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SyncShopifyButton } from '@/components/dashboard/SyncShopifyButton'
import {
  useLifetimeSpikeSettings,
  useSetLifetimeSpikeSettings,
  useRecQtyRoundingSettings,
  useSetRecQtyRoundingSettings,
  useShopifySyncSetting,
  useSetShopifySyncSetting,
  type LifetimeSpikeSettings,
} from '@/hooks/useAdmin'
import { fmtDateTime } from '@/lib/dates'

const PILL = 'inline-block rounded-full text-[10.5px] font-semibold px-2 py-0.5 leading-none'

// The size-level N (units_60d_max) isn't editable: no size-level flag is
// shown since L1b. The setting is kept, and saves send its stored value back.
type SpikeField = Exclude<keyof LifetimeSpikeSettings, 'units_60d_max'>

const SPIKE_FIELDS: { key: SpikeField; label: string; rule: string; valid: (n: number) => boolean }[] = [
  { key: 'ratio_min', label: 'vs usual at least (K)', rule: 'K must be a number above 1', valid: (n) => n > 1 },
  { key: 'product_units_60d_max', label: '60-day units at most (N), across all sizes', rule: 'N must be a whole number, 0 or more', valid: (n) => Number.isInteger(n) && n >= 0 },
  { key: 'min_days', label: 'Available at least, days (M)', rule: 'M must be a whole number, 1 or more', valid: (n) => Number.isInteger(n) && n >= 1 },
]

/** K/N/M for the dashboard's "Check history" flag. Saving marks the
 *  dashboard stale; it rebuilds with the new thresholds in the background. */
function LifetimeSpikeSettingsCard() {
  const { data } = useLifetimeSpikeSettings()
  const setMut = useSetLifetimeSpikeSettings()
  const [draft, setDraft] = useState<Partial<Record<SpikeField, string>>>({})

  const raw = (key: SpikeField) => draft[key] ?? (data ? String(data[key]) : '')
  const errors = SPIKE_FIELDS.filter((f) => {
    const v = raw(f.key).trim()
    return v === '' || !Number.isFinite(Number(v)) || !f.valid(Number(v))
  }).map((f) => f.rule)
  const dirty = SPIKE_FIELDS.some((f) => f.key in draft && Number(draft[f.key]) !== data?.[f.key])

  async function save() {
    if (!data) return
    try {
      await setMut.mutateAsync({
        ratio_min: Number(raw('ratio_min')),
        units_60d_max: data.units_60d_max,
        min_days: Number(raw('min_days')),
        product_units_60d_max: Number(raw('product_units_60d_max')),
      })
      setDraft({})
      toast.success('Check history thresholds saved. The dashboard is rebuilding with them.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'update failed')
    }
  }

  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 space-y-2" data-testid="lifetime-spike-settings">
      <div>
        <div className="text-xs font-medium">Check history flag</div>
        <div className="text-xs text-[var(--mute)]">
          Marks a dashboard row "Check" when its product (all sizes, per colour or style) is selling at
          least K times its usual pace, sold at most N units across all its sizes in the last 60 days, and has
          been available at least M days. Display only: it
          doesn't change Rec Qty, signals or any forecast.
        </div>
      </div>
      <div className="flex items-end gap-2">
        {SPIKE_FIELDS.map((f) => (
          <label key={f.key} className="text-[10px] flex flex-col">
            <span className="text-[var(--mute)]">{f.label}</span>
            <input
              type="number"
              aria-label={f.label}
              value={raw(f.key)}
              onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
              className="w-28 px-1 py-0.5 border border-[var(--line-input)] bg-[var(--surface-card)] text-xs text-right tabular-nums focus:outline-none focus:border-[var(--brand)]"
            />
          </label>
        ))}
        <div className="flex-1" />
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={!data || !dirty || errors.length > 0 || setMut.isPending}
          onClick={save}
          data-testid="lifetime-spike-save"
        >
          {setMut.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {data && errors.length > 0 && (
        <div className="text-xs text-[var(--status-rejected-fg)]" data-testid="lifetime-spike-error">
          {errors.join('. ')}
        </div>
      )}
    </div>
  )
}

const ROUNDING_LABEL = 'Ignore a shortfall smaller than (fraction of a unit)'

/** Rec Qty rounding threshold X (brief R1): Rec Qty = ceil(shortfall − X).
 *  Saving marks the dashboard stale; it rebuilds with the new X. */
function RecQtyRoundingCard() {
  const { data } = useRecQtyRoundingSettings()
  const setMut = useSetRecQtyRoundingSettings()
  const [draft, setDraft] = useState<string | null>(null)

  const raw = draft ?? (data ? String(data.threshold) : '')
  const n = Number(raw.trim())
  const valid = raw.trim() !== '' && Number.isFinite(n) && n >= 0 && n < 1
  const dirty = draft !== null && n !== data?.threshold

  async function save() {
    try {
      await setMut.mutateAsync({ threshold: n })
      setDraft(null)
      toast.success('Rec Qty rounding saved. The dashboard is rebuilding with it.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'update failed')
    }
  }

  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 space-y-2" data-testid="rec-qty-rounding-settings">
      <div>
        <div className="text-xs font-medium">Rec Qty rounding</div>
        <div className="text-xs text-[var(--mute)]">
          0 = always round up (current behaviour); 0.5 = round to nearest. A row whose shortfall is
          dropped to 0 units shows OK instead of ORDER NOW.
        </div>
      </div>
      <div className="flex items-end gap-2">
        <label className="text-[10px] flex flex-col">
          <span className="text-[var(--mute)]">{ROUNDING_LABEL}</span>
          <input
            type="number"
            step="0.05"
            min="0"
            max="0.99"
            aria-label={ROUNDING_LABEL}
            value={raw}
            onChange={(e) => setDraft(e.target.value)}
            className="w-28 px-1 py-0.5 border border-[var(--line-input)] bg-[var(--surface-card)] text-xs text-right tabular-nums focus:outline-none focus:border-[var(--brand)]"
          />
        </label>
        <div className="flex-1" />
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={!data || !dirty || !valid || setMut.isPending}
          onClick={save}
          data-testid="rec-qty-rounding-save"
        >
          {setMut.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {data && !valid && (
        <div className="text-xs text-[var(--status-rejected-fg)]" data-testid="rec-qty-rounding-error">
          Must be a number from 0 up to (not including) 1
        </div>
      )}
    </div>
  )
}

export function SettingsSection() {
  const { data: setting } = useShopifySyncSetting()
  const setMut = useSetShopifySyncSetting()
  const enabled = setting?.enabled ?? true
  const [confirmOff, setConfirmOff] = useState(false)

  async function setEnabled(next: boolean) {
    try {
      const res = await setMut.mutateAsync(next)
      toast.success(`PO Shopify Sync ${next ? 'enabled' : 'disabled'}`)
      // Nothing is replayed when sync comes back on, so name the leftover
      // work. The PUT may not echo the count; fall back to the GET's.
      const unsynced = res?.unsynced_po_count ?? setting?.unsynced_po_count ?? 0
      if (next && unsynced > 0) {
        toast.warning(
          `Sync is on. ${unsynced} PO${unsynced === 1 ? '' : 's'} have lines that didn't reach Shopify — use Retry Sync on each.`
        )
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'update failed'
      toast.error(msg)
    }
  }

  function toggle() {
    // Turning OFF is the risky direction — confirm first. Turning ON is
    // always safe, so it stays one click.
    if (enabled) setConfirmOff(true)
    else void setEnabled(true)
  }

  async function confirmTurnOff() {
    await setEnabled(false)
    setConfirmOff(false)
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">App Settings</h2>
      <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 flex items-center gap-3">
        <div>
          <div className="text-xs font-medium">PO Shopify Sync</div>
          <div className="text-xs text-[var(--mute)]">
            When off, purchase-order changes stop reaching Shopify: marking ordered, receiving
            (Save &amp; exit), archiving, unarchiving, and quantity edits, line deletes and
            backorders on placed POs. Stocktakes, adjustments, manual inventory and barcode
            changes still sync. Changes made while off are NOT sent automatically when it's
            turned back on: affected POs show 'Not synced'; use Retry Sync on each.
          </div>
        </div>
        <div className="flex-1" />
        {/* State is a pill (green On / grey Off) and the action is a plain
            outline button that names what it will do. The old single button
            was loud when OFF and quiet when ON, which read backwards for the
            most consequential switch in the app (owner-approved change). */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            data-testid="shopify-sync-state"
            className={`${PILL} ${
              enabled
                ? 'bg-[var(--status-approved-bg)] text-[var(--status-approved-fg)]'
                : 'bg-[var(--status-archived-bg)] text-[var(--status-archived-fg)]'
            }`}
          >
            {enabled ? 'On' : 'Off'}
          </span>
          {!enabled && (setting?.changed_at || setting?.changed_by) && (
            <span className="text-[10.5px] text-[var(--mute)] whitespace-nowrap" data-testid="shopify-sync-since">
              Off{setting?.changed_at ? ` since ${fmtDateTime(setting.changed_at)}` : ''}
              {setting?.changed_by ? ` by ${setting.changed_by}` : ''}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={toggle}
          disabled={setMut.isPending}
          data-testid="shopify-sync-toggle"
        >
          {enabled ? 'Turn off' : 'Turn on'}
        </Button>
      </div>
      <Dialog open={confirmOff} onOpenChange={(open) => { if (!open) setConfirmOff(false) }}>
        <DialogContent
          className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)]"
          data-testid="shopify-sync-off-dialog"
        >
          <DialogHeader>
            <DialogTitle>Turn off Shopify sync?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-xs">
                <p>
                  Purchase-order changes will stop reaching Shopify: marking ordered, receiving
                  (Save &amp; exit), archiving, unarchiving, and quantity edits, line deletes and
                  backorders on placed POs.
                </p>
                <p>Stocktakes, adjustments, manual inventory and barcode changes still sync.</p>
                <p>
                  Nothing is replayed automatically. Affected POs will show 'Not synced'; use Retry
                  Sync on each after turning sync back on.
                </p>
                <p className="font-semibold text-[var(--status-awaiting-fg)]">
                  While sync is off, don't delete lines from placed POs, backorder lines, or
                  unarchive POs. Those changes can't be recovered afterwards — Shopify's incoming
                  stock will stay wrong and Retry Sync can't fix it.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOff(false)}
              disabled={setMut.isPending}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={confirmTurnOff} disabled={setMut.isPending}>
              Turn off sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 flex items-center gap-3">
        <div>
          <div className="text-xs font-medium">Sync from Shopify</div>
          <div className="text-xs text-[var(--mute)]">Stock levels, incoming and last 30 days of sales. ~2 min.</div>
          <div className="text-xs text-[var(--mute)]">Full re-sync (menu) also refreshes products, costs, new SKUs and 60–120-day sales. ~6 min.</div>
        </div>
        <div className="flex-1" />
        <SyncShopifyButton />
      </div>
      <LifetimeSpikeSettingsCard />
      <RecQtyRoundingCard />
    </section>
  )
}
